import { useEffect, useState, type PropsWithChildren } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Building2Icon,
  CalendarIcon,
  ChevronDownIcon,
  EllipsisIcon,
  GaugeIcon,
  HourglassIcon,
  Loader2Icon,
  MailIcon,
  PlusIcon,
  RefreshCcwIcon,
  Settings2Icon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  Trash2Icon,
  UserCheckIcon,
  UserMinusIcon,
  UsersIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  banUser,
  changeUserRole,
  removeUser,
  type AdminUserRecord,
  authClient,
  getBrowserUrl,
  sendActivationEmailAsAdmin,
  unbanUser,
  updateDemoExpiration,
} from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useAdminUsers } from '@/hooks/use-admin-users';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  MembersManagementHeader,
  MembersManagementPagination,
  MembersManagementPanel,
  MembersManagementTable,
  MembersManagementToolbar,
  MembersToolbarButton,
  type MembersManagementTableState,
  type MembersTableColumn,
} from '@/components/common/members-management';
import { UsersTableColumnsPopover, type UsersTableColumnOption } from '@/components/users-table-columns-popover';
import { AccountTypeBadge } from '@/features/quota/components/QuotaInconsistentBadge';
import { QuotaMiniProgress } from '@/features/quota/components/QuotaMiniProgress';
import { formatCompactNumber } from '@/features/usage/lib/format';

type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;
type SortKey = 'accountType' | 'createdAt' | 'email' | 'emailVerified' | 'name' | 'role' | 'status' | 'updatedAt';
type SortDirection = 'asc' | 'desc';
type RoleFilter = 'all' | 'admin' | 'user';
type AccountTypeFilter = 'all' | 'demo' | 'subscriber';
type StatusFilter = 'all' | 'active' | 'disabled';
type VerificationFilter = 'all' | 'pending' | 'verified';
type UserTableColumnKey = 'accountType' | 'company' | 'consumed' | 'createdAt' | 'email' | 'emailVerified' | 'name' | 'quota' | 'role' | 'status';

type UsersManagementPageProps = {
  session: SessionData;
};

type SortPreset = `${SortKey}:${SortDirection}`;

const roleFilterOptions: Array<{ label: string; value: RoleFilter }> = [
  { label: 'Tous les rôles', value: 'all' },
  { label: 'Super admins', value: 'admin' },
  { label: 'Utilisateurs', value: 'user' },
];

const accountTypeFilterOptions: Array<{ label: string; value: AccountTypeFilter }> = [
  { label: 'Tous les types', value: 'all' },
  { label: 'Comptes démo', value: 'demo' },
  { label: 'Abonnés', value: 'subscriber' },
];

const statusFilterOptions: Array<{ label: string; value: StatusFilter }> = [
  { label: 'Tous les statuts', value: 'all' },
  { label: 'Actifs', value: 'active' },
  { label: 'Désactivés', value: 'disabled' },
];

const verificationFilterOptions: Array<{ label: string; value: VerificationFilter }> = [
  { label: 'Toutes les vérifications', value: 'all' },
  { label: 'Vérifiés', value: 'verified' },
  { label: 'En attente', value: 'pending' },
];

const pageSizeOptions = [10, 25, 50] as const;

const sortPresetOptions: Array<{ label: string; value: SortPreset }> = [
  { label: 'Ordre personnalisé', value: 'createdAt:desc' },
  { label: 'Nom (A-Z)', value: 'name:asc' },
  { label: 'Nom (Z-A)', value: 'name:desc' },
  { label: 'Dernière mise à jour', value: 'updatedAt:desc' },
  { label: 'Vérifiés d’abord', value: 'emailVerified:desc' },
  { label: 'Comptes actifs d’abord', value: 'status:asc' },
];

const USERS_TABLE_COLUMNS_STORAGE_KEY = 'vibecode-admin-users-table-columns';

const defaultTableColumns: Array<UsersTableColumnOption & { key: UserTableColumnKey }> = [
  { key: 'name', label: 'Nom', visible: false },
  { key: 'email', label: 'Email', visible: true },
  { key: 'company', label: 'Entreprise', visible: true },
  { key: 'accountType', label: 'Type', visible: true },
  { key: 'quota', label: 'Quota', visible: true },
  { key: 'consumed', label: 'Consommation', visible: true },
  { key: 'role', label: 'Rôle', visible: true },
  { key: 'status', label: 'Statut', visible: true },
  { key: 'emailVerified', label: 'Vérification', visible: true },
  { key: 'createdAt', label: 'Créé le', visible: true },
];

function readStoredTableColumns(): typeof defaultTableColumns {
  if (typeof window === 'undefined') {
    return defaultTableColumns;
  }

  try {
    const raw = window.localStorage.getItem(USERS_TABLE_COLUMNS_STORAGE_KEY);

    if (!raw) {
      return defaultTableColumns;
    }

    const parsed = JSON.parse(raw) as Array<Partial<UsersTableColumnOption> & { key?: string }>;

    if (!Array.isArray(parsed)) {
      return defaultTableColumns;
    }

    const defaultColumnsMap = new Map(defaultTableColumns.map((column) => [column.key, column]));
    const normalizedColumns = parsed
      .map((column) => {
        if (!column.key) {
          return null;
        }

        const defaultColumn = defaultColumnsMap.get(column.key as UserTableColumnKey);

        if (!defaultColumn) {
          return null;
        }

        return {
          ...defaultColumn,
          visible: typeof column.visible === 'boolean' ? column.visible : defaultColumn.visible,
        };
      })
      .filter((column): column is (typeof defaultTableColumns)[number] => column !== null);

    const missingColumns = defaultTableColumns.filter(
      (defaultColumn) => !normalizedColumns.some((column) => column.key === defaultColumn.key),
    );

    const mergedColumns = [...normalizedColumns, ...missingColumns];
    const visibleCount = mergedColumns.filter((column) => column.visible).length;

    if (visibleCount === 0) {
      return defaultTableColumns;
    }

    return mergedColumns;
  } catch {
    return defaultTableColumns;
  }
}

const demoExpirationChoices = [
  { days: 7, label: '1 semaine', value: '1w' },
  { days: 30, label: '1 mois', value: '1m' },
  { days: 90, label: '3 mois', value: '3m' },
  { days: null, label: 'Sans expiration', value: 'none' },
] as const;

type DemoExpirationChoice = (typeof demoExpirationChoices)[number]['value'];

function isDemoExpired(user: AdminUserRecord) {
  return (user.accountType ?? 'subscriber') === 'demo'
    && user.demoExpiresAt != null
    && new Date(user.demoExpiresAt).getTime() <= Date.now();
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function SortHeaderButton({
  active,
  children,
  direction,
  onClick,
}: PropsWithChildren<{
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 font-medium text-foreground transition-colors hover:text-muted-foreground"
    >
      <span>{children}</span>
      {active ? (
        direction === 'asc' ? <ArrowUpIcon className="size-3.5 text-muted-foreground" /> : <ArrowDownIcon className="size-3.5 text-muted-foreground" />
      ) : (
        <ArrowDownIcon className="size-3.5 text-muted-foreground/50" />
      )}
    </button>
  );
}

function TruncatedValue({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('block min-w-0 max-w-full truncate', className)}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        <span>{children}</span>
      </TooltipContent>
    </Tooltip>
  );
}

// Le rôle plateforme (super admin) prime ; sinon on affiche le rôle dans l'entreprise.
function getUserRoleDisplay(user: AdminUserRecord): {
  kind: 'company-admin' | 'platform-admin' | 'user';
  label: string;
} {
  if (user.role === 'admin') return { kind: 'platform-admin', label: 'Super admin' };
  if (user.companyRole === 'owner') return { kind: 'company-admin', label: 'Propriétaire' };
  if (user.companyRole === 'admin') return { kind: 'company-admin', label: 'Admin entreprise' };
  return { kind: 'user', label: 'Utilisateur' };
}

function getSortPresetLabel(sortKey: SortKey, sortDirection: SortDirection) {
  const preset = `${sortKey}:${sortDirection}` as SortPreset;
  return sortPresetOptions.find((option) => option.value === preset)?.label ?? 'Ordre personnalisé';
}

export function UsersManagementPage({ session }: UsersManagementPageProps) {
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [expirationTarget, setExpirationTarget] = useState<AdminUserRecord | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<
    { kind: 'delete' | 'disable' | 'role'; user: AdminUserRecord } | null
  >(null);
  const [expirationChoice, setExpirationChoice] = useState<DemoExpirationChoice>('1m');
  const [isExpirationSaving, setIsExpirationSaving] = useState(false);
  const [tableColumns, setTableColumns] = useState<typeof defaultTableColumns>(() => readStoredTableColumns());
  const [searchValue, setSearchValue] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(10);
  const debouncedSearchValue = useDebouncedValue(searchValue, 280);
  const currentUserId = session.user.id;

  const { data: usersResponse, isLoading, isRefreshing, refresh: refreshUsers } = useAdminUsers(currentUserId, {
    accountType: accountTypeFilter,
    page,
    pageSize,
    role: roleFilter,
    search: debouncedSearchValue || undefined,
    sortBy: sortKey,
    sortDirection,
    status: statusFilter,
    verification: verificationFilter,
  });

  useEffect(() => {
    if (!isLoading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- keeps the table skeleton aligned with the external query loading state.
      setShowLoadingSkeleton(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoadingSkeleton(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoading]);

  const users = usersResponse.items;
  const { meta, summary } = usersResponse;
  const currentRangeStart = meta.filteredTotal === 0 ? 0 : (meta.page - 1) * meta.pageSize + 1;
  const currentRangeEnd = Math.min(meta.page * meta.pageSize, meta.filteredTotal);

  function clearRoleFilter() {
    setRoleFilter('all');
    setPage(1);
  }

  function clearAccountTypeFilter() {
    setAccountTypeFilter('all');
    setPage(1);
  }

  function clearStatusFilter() {
    setStatusFilter('all');
    setPage(1);
  }

  function clearVerificationFilter() {
    setVerificationFilter('all');
    setPage(1);
  }

  const activeFilters = [
    roleFilter !== 'all'
      ? {
          key: 'role',
          label: roleFilterOptions.find((option) => option.value === roleFilter)?.label ?? roleFilter,
          onClear: clearRoleFilter,
        }
      : null,
    accountTypeFilter !== 'all'
      ? {
          key: 'accountType',
          label: accountTypeFilterOptions.find((option) => option.value === accountTypeFilter)?.label ?? accountTypeFilter,
          onClear: clearAccountTypeFilter,
        }
      : null,
    statusFilter !== 'all'
      ? {
          key: 'status',
          label: statusFilterOptions.find((option) => option.value === statusFilter)?.label ?? statusFilter,
          onClear: clearStatusFilter,
        }
      : null,
    verificationFilter !== 'all'
      ? {
          key: 'verification',
          label: verificationFilterOptions.find((option) => option.value === verificationFilter)?.label ?? verificationFilter,
          onClear: clearVerificationFilter,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClear: () => void }>;

  async function handleDisableUser(user: AdminUserRecord) {
    const result = await banUser(user.id, 'Compte désactivé par un administrateur.');

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Utilisateur désactivé.');
    await refreshUsers();
  }

  async function handleEnableUser(user: AdminUserRecord) {
    const result = await unbanUser(user.id);

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Utilisateur réactivé.');
    await refreshUsers();
  }

  function openDemoExpirationDialog(user: AdminUserRecord) {
    setExpirationChoice('1m');
    setExpirationTarget(user);
  }

  async function handleSaveDemoExpiration() {
    if (!expirationTarget) {
      return;
    }

    const choice = demoExpirationChoices.find((option) => option.value === expirationChoice);
    const demoExpiresAt = choice?.days == null
      ? null
      : new Date(Date.now() + choice.days * 24 * 60 * 60 * 1000).toISOString();

    setIsExpirationSaving(true);
    const result = await updateDemoExpiration(expirationTarget.id, { demoExpiresAt });
    setIsExpirationSaving(false);

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success(demoExpiresAt
      ? `Compte démo actif jusqu’au ${formatDate(demoExpiresAt)}.`
      : 'Compte démo réactivé sans date d’expiration.');
    setExpirationTarget(null);
    await refreshUsers();
  }

  async function handleDeleteUser(user: AdminUserRecord) {
    const result = await removeUser(user.id);

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Utilisateur supprimé.');

    if (users.length === 1 && page > 1) {
      setPage((current) => current - 1);
      return;
    }

    await refreshUsers();
  }

  async function handleResendVerificationEmail(user: AdminUserRecord) {
    const result = await sendActivationEmailAsAdmin({
      callbackURL: getBrowserUrl(`/auth/set-password?email=${encodeURIComponent(user.email)}`),
      email: user.email,
    });

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Email d’activation renvoyé.');
  }

  async function handleToggleRole(user: AdminUserRecord) {
    const nextRole = user.role === 'admin' ? 'user' : 'admin';

    const result = await changeUserRole({
      role: nextRole,
      userId: user.id,
    });

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success(nextRole === 'admin' ? 'Utilisateur promu admin.' : 'Utilisateur repassé en user.');
    await refreshUsers();
  }

  function handleSearchChange(value: string) {
    setSearchValue(value);
    setPage(1);
  }

  function handlePageSizeChange(value: (typeof pageSizeOptions)[number]) {
    setPageSize(value);
    setPage(1);
  }

  function handleSortPresetChange(value: string) {
    const [nextSortKey, nextSortDirection] = value.split(':') as [SortKey, SortDirection];
    setSortKey(nextSortKey);
    setSortDirection(nextSortDirection);
    setPage(1);
  }

  function toggleSort(nextKey: SortKey) {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      setPage(1);
      return;
    }

    setSortKey(nextKey);
    setSortDirection(nextKey === 'name' ? 'asc' : 'desc');
    setPage(1);
  }

  function resetControls() {
    setSearchValue('');
    clearRoleFilter();
    clearAccountTypeFilter();
    clearStatusFilter();
    clearVerificationFilter();
    setSortKey('createdAt');
    setSortDirection('desc');
    setPage(1);
  }

  const currentSortPreset = `${sortKey}:${sortDirection}` as SortPreset;
  const sortPresetLabel = getSortPresetLabel(sortKey, sortDirection);
  const visibleTableColumns = tableColumns.filter((column) => column.visible);

  useEffect(() => {
    window.localStorage.setItem(USERS_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(tableColumns));
  }, [tableColumns]);

  function resetTableColumns() {
    setTableColumns(defaultTableColumns);
  }

  function renderHeaderCell(column: (typeof defaultTableColumns)[number]) {
    switch (column.key) {
      case 'name':
        return (
          <SortHeaderButton active={sortKey === 'name'} direction={sortDirection} onClick={() => toggleSort('name')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'email':
        return (
          <SortHeaderButton active={sortKey === 'email'} direction={sortDirection} onClick={() => toggleSort('email')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'company':
        return (
          <span className="font-medium text-foreground">
            {column.label}
          </span>
        );
      case 'accountType':
        return (
          <SortHeaderButton active={sortKey === 'accountType'} direction={sortDirection} onClick={() => toggleSort('accountType')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'quota':
        return (
          <span className="font-medium text-foreground">
            {column.label}
          </span>
        );
      case 'consumed':
        return (
          <span className="font-medium text-foreground">
            {column.label}
          </span>
        );
      case 'role':
        return (
          <SortHeaderButton active={sortKey === 'role'} direction={sortDirection} onClick={() => toggleSort('role')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'status':
        return (
          <SortHeaderButton active={sortKey === 'status'} direction={sortDirection} onClick={() => toggleSort('status')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'emailVerified':
        return (
          <SortHeaderButton active={sortKey === 'emailVerified'} direction={sortDirection} onClick={() => toggleSort('emailVerified')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'createdAt':
        return (
          <SortHeaderButton active={sortKey === 'createdAt'} direction={sortDirection} onClick={() => toggleSort('createdAt')}>
            {column.label}
          </SortHeaderButton>
        );
    }
  }

  function renderSkeletonCell(columnKey: UserTableColumnKey) {
    switch (columnKey) {
      case 'name':
        return (
          <div className="flex items-center gap-4">
            <Skeleton className="size-16 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-48 rounded-full" />
              <Skeleton className="h-4 w-24 rounded-full" />
            </div>
          </div>
        );
      case 'email':
        return <Skeleton className="h-5 w-56 rounded-full" />;
      case 'company':
        return <Skeleton className="h-5 w-40 rounded-full" />;
      case 'accountType':
        return <Skeleton className="h-8 w-24 rounded-full" />;
      case 'quota':
        return <Skeleton className="h-10 w-36 rounded-full" />;
      case 'consumed':
        return <Skeleton className="h-5 w-24 rounded-full" />;
      case 'role':
        return <Skeleton className="h-8 w-28 rounded-full" />;
      case 'status':
        return <Skeleton className="h-8 w-24 rounded-full" />;
      case 'emailVerified':
        return <Skeleton className="h-8 w-32 rounded-full" />;
      case 'createdAt':
        return <Skeleton className="h-5 w-36 rounded-full" />;
    }
  }

  function renderUserCell(columnKey: UserTableColumnKey, user: AdminUserRecord, isCurrentUser: boolean) {
    switch (columnKey) {
      case 'name':
        return (
          <div className="flex min-h-16 min-w-0 items-center gap-2">
            <TruncatedValue className="max-w-[18rem] text-[15px] font-semibold text-foreground">
              {user.name}
            </TruncatedValue>
            {isCurrentUser ? (
              <span className="rounded-full bg-foreground px-2.5 py-1 text-xs font-medium text-background">
                Vous
              </span>
            ) : null}
          </div>
        );
      case 'email':
        return (
          <div className="flex min-h-16 items-center">
            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
              <MailIcon className="-translate-y-px size-4 shrink-0 text-muted-foreground" />
              <TruncatedValue className="max-w-[14rem] leading-none">
                {user.email}
              </TruncatedValue>
            </div>
          </div>
        );
      case 'company':
        return (
          <div className="flex min-h-16 items-center">
            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
              <Building2Icon className="-translate-y-px size-4 shrink-0 text-muted-foreground" />
              <TruncatedValue className="max-w-[14rem] leading-none">
                {user.companyName ?? '—'}
              </TruncatedValue>
            </div>
          </div>
        );
      case 'accountType': {
        const accountType = user.accountType ?? 'subscriber';

        return (
          <div className="flex min-h-16 items-center">
            <AccountTypeBadge accountType={accountType} />
          </div>
        );
      }
      case 'quota': {
        const accountType = user.accountType ?? 'subscriber';

        if (accountType === 'subscriber') {
          return (
            <div className="flex min-h-16 items-center text-sm text-muted-foreground">
              Illimité
            </div>
          );
        }

        if (!user.quota) {
          return (
            <div className="flex min-h-16 items-center text-sm text-muted-foreground">
              {user.demoExpiresAt
                ? `Expire le ${formatDate(user.demoExpiresAt)}`
                : 'Sans expiration'}
            </div>
          );
        }

        return (
          <div className="flex min-h-16 items-center py-1">
            <QuotaMiniProgress
              consumed={user.quota.tokensConsumed}
              extraTokens={user.quota.extraTokens}
              renewsAt={user.quota.renewsAt}
              tokenLimit={user.quota.tokenLimit}
            />
          </div>
        );
      }
      case 'consumed':
        // Cumul tous usages confondus, indépendant d'un quota configuré.
        return (
          <div
            className="flex min-h-16 items-center text-sm tabular-nums"
            title={`${(user.tokensConsumed ?? 0).toLocaleString('fr-FR')} tokens`}
          >
            {`${formatCompactNumber(user.tokensConsumed ?? 0)} tokens`}
          </div>
        );
      case 'role': {
        const roleDisplay = getUserRoleDisplay(user);
        return (
          <div className="flex min-h-16 items-center">
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium whitespace-nowrap',
              roleDisplay.kind === 'user' ? 'bg-muted text-muted-foreground' : 'bg-secondary text-secondary-foreground',
            )}
            >
              {roleDisplay.kind === 'platform-admin' ? <ShieldCheckIcon className="size-3.5" /> : null}
              {roleDisplay.kind === 'company-admin' ? <Building2Icon className="size-3.5" /> : null}
              {roleDisplay.kind === 'user' ? <UsersIcon className="size-3.5" /> : null}
              {roleDisplay.label}
            </span>
          </div>
        );
      }
      case 'status': {
        const expiredDemo = !user.banned && isDemoExpired(user);
        return (
          <div className="flex min-h-16 items-center">
            <span className={cn(
              'inline-flex rounded-full px-2.5 py-1 text-sm font-medium',
              user.banned
                ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
                : expiredDemo
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
            )}
            >
              {user.banned ? 'Désactivé' : expiredDemo ? 'Expiré' : 'Actif'}
            </span>
          </div>
        );
      }
      case 'emailVerified':
        return (
          <div className="flex min-h-16 items-center">
            <span className={cn(
              'inline-flex rounded-full px-2.5 py-1 text-sm font-medium',
              user.emailVerified
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
            )}
            >
              {user.emailVerified ? 'Vérifié' : 'En attente'}
            </span>
          </div>
        );
      case 'createdAt':
        return (
          <div className="flex min-h-16 items-center">
            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
              <CalendarIcon className="-translate-y-px size-4 shrink-0 text-muted-foreground" />
              <TruncatedValue className="max-w-[8rem] leading-none">{formatDate(user.createdAt)}</TruncatedValue>
            </div>
          </div>
        );
    }
  }

  function renderUserActions(user: AdminUserRecord) {
    const isCurrentUser = user.id === currentUserId;

    return (
      <div className="flex items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon-sm"
              className="size-10 border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
              data-testid={`user-actions-${user.id}`}
              aria-label={`Actions pour ${user.email}`}
            >
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Actions utilisateur</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={`/admin/users/${user.id}/quota`} data-testid={`manage-quota-${user.id}`}>
                  <GaugeIcon />
                  Gérer le quota
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                disabled={isCurrentUser}
                onClick={() => setPendingConfirm({ kind: 'role', user })}
              >
                {user.role === 'admin' ? <ShieldAlertIcon /> : <ShieldCheckIcon />}
                {user.role === 'admin' ? 'Rétrograder en user' : 'Promouvoir admin'}
              </DropdownMenuItem>
              {user.banned ? (
                <DropdownMenuItem onClick={() => void handleEnableUser(user)}>
                  <UserCheckIcon />
                  Réactiver le compte
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled={isCurrentUser}
                  onClick={() => setPendingConfirm({ kind: 'disable', user })}
                >
                  <UserMinusIcon />
                  Désactiver le compte
                </DropdownMenuItem>
              )}
              {(user.accountType ?? 'subscriber') === 'demo' ? (
                <DropdownMenuItem
                  data-testid={`demo-expiration-${user.id}`}
                  onClick={() => openDemoExpirationDialog(user)}
                >
                  <HourglassIcon />
                  {isDemoExpired(user) ? 'Réactiver le compte démo' : 'Modifier l’expiration démo'}
                </DropdownMenuItem>
              ) : null}
              {!user.emailVerified ? (
                <DropdownMenuItem onClick={() => void handleResendVerificationEmail(user)}>
                  <RefreshCcwIcon />
                  Renvoyer l’email
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                variant="destructive"
                disabled={isCurrentUser}
                onClick={() => setPendingConfirm({ kind: 'delete', user })}
              >
                <Trash2Icon />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  const membersTableColumns: MembersTableColumn<AdminUserRecord>[] = visibleTableColumns.map((column) => ({
    key: column.key,
    header: renderHeaderCell(column),
    renderCell: (user) => renderUserCell(column.key, user, user.id === currentUserId),
    renderSkeleton: () => renderSkeletonCell(column.key),
  }));

  const tableState: MembersManagementTableState = isLoading
    ? { status: 'loading', showSkeleton: showLoadingSkeleton }
    : users.length === 0
      ? {
          status: 'empty',
          content: (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UsersIcon className="size-6" />
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold text-foreground">Aucun membre ne correspond à cette vue</h3>
                <p className="max-w-md text-sm leading-6 text-muted-foreground">
                  Ajuste la recherche, les filtres ou l’ordre d’affichage pour retrouver rapidement le bon compte.
                </p>
              </div>
            </div>
          ),
        }
      : { status: isRefreshing ? 'refreshing' : 'success' };

  const skeletonRowCount = Math.min(pageSize, 4);

  return (
    <section className="space-y-6 text-foreground">
      <MembersManagementHeader
        title="Liste des utilisateurs"
        description="Gestion globale des comptes, invitations et rôles de l'application."
        count={meta.filteredTotal}
        actions={(
          <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <MembersToolbarButton>
                Options
                <ChevronDownIcon data-icon="inline-end" />
              </MembersToolbarButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuGroup>
                <DropdownMenuLabel>Options de page</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void refreshUsers()} disabled={isLoading || isRefreshing}>
                  {isRefreshing ? <Loader2Icon className="animate-spin" /> : <RefreshCcwIcon />}
                  Actualiser la liste
                </DropdownMenuItem>
                <DropdownMenuItem onClick={resetControls}>
                  <Settings2Icon />
                  Réinitialiser recherche et filtres
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            asChild
            variant="outline"
            data-testid="users-add-button"
            className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
          >
            <Link to="/admin/users/new">
              <PlusIcon data-icon="inline-start" />
              Créer un utilisateur
            </Link>
          </Button>
          </>
        )}
      />

      <MembersManagementPanel
        toolbar={(
          <MembersManagementToolbar
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Rechercher les membres de l’équipe"
            searchInputTestId="users-search-input"
            filterControl={(
              <MembersToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="shrink-0 gap-2 px-4">
                Filtres
                <Settings2Icon data-icon="inline-end" />
              </MembersToolbarButton>
            )}
            sortControl={(
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <MembersToolbarButton className="justify-between xl:min-w-56">
                    {sortPresetLabel}
                    <ChevronDownIcon data-icon="inline-end" />
                  </MembersToolbarButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup value={currentSortPreset} onValueChange={handleSortPresetChange}>
                      {sortPresetOptions.map((option) => (
                        <DropdownMenuRadioItem key={option.value} value={option.value}>
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            columnsControl={(
              <UsersTableColumnsPopover
                columns={tableColumns}
                onChange={(columns) => setTableColumns(columns as typeof defaultTableColumns)}
                onReset={resetTableColumns}
              />
            )}
            activeFilters={activeFilters}
            onClearAllFilters={() => {
              clearRoleFilter();
              clearAccountTypeFilter();
              clearStatusFilter();
              clearVerificationFilter();
            }}
          />
        )}

        table={(
          <MembersManagementTable
            columns={membersTableColumns}
            rows={users}
            getRowKey={(user) => user.id}
            getRowTestId={(user) => `user-row-${user.id}`}
            state={tableState}
            skeletonRowCount={skeletonRowCount}
            renderActions={renderUserActions}
          />
        )}

        pagination={(
          <MembersManagementPagination
            id="users-pagination"
            page={meta.page}
            pageCount={meta.pageCount}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            totalLabel={(
              <>
                {currentRangeStart}-{currentRangeEnd} sur {meta.filteredTotal} membres affichés ({summary.totalUsers} au total)
              </>
            )}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      />

      <AlertDialog
        open={pendingConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setPendingConfirm(null);
        }}
      >
        <AlertDialogContent className="max-w-md rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingConfirm?.kind === 'delete'
                ? 'Supprimer l’utilisateur ?'
                : pendingConfirm?.kind === 'disable'
                  ? 'Désactiver le compte ?'
                  : pendingConfirm?.user.role === 'admin'
                    ? 'Rétrograder en utilisateur ?'
                    : 'Promouvoir en super admin ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingConfirm?.kind === 'delete'
                ? `${pendingConfirm.user.email} sera supprimé définitivement. Cette action est irréversible.`
                : pendingConfirm?.kind === 'disable'
                  ? `${pendingConfirm.user.email} ne pourra plus se connecter et toutes ses sessions seront révoquées.`
                  : pendingConfirm?.user.role === 'admin'
                    ? `${pendingConfirm?.user.email} perdra l’accès super admin et redeviendra un utilisateur standard.`
                    : `${pendingConfirm?.user.email} obtiendra tous les droits de l’application : entreprises et utilisateurs.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-confirm-action"
              className={pendingConfirm?.kind === 'role'
                ? undefined
                : 'bg-destructive text-white hover:bg-destructive/90'}
              onClick={() => {
                const confirmTarget = pendingConfirm;
                setPendingConfirm(null);
                if (!confirmTarget) return;
                if (confirmTarget.kind === 'delete') void handleDeleteUser(confirmTarget.user);
                else if (confirmTarget.kind === 'disable') void handleDisableUser(confirmTarget.user);
                else void handleToggleRole(confirmTarget.user);
              }}
            >
              {pendingConfirm?.kind === 'delete'
                ? 'Supprimer'
                : pendingConfirm?.kind === 'disable'
                  ? 'Désactiver'
                  : 'Confirmer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={expirationTarget !== null}
        onOpenChange={(open) => {
          if (!open && !isExpirationSaving) {
            setExpirationTarget(null);
          }
        }}
      >
        <DialogContent showCloseButton={!isExpirationSaving} className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>
              {expirationTarget && isDemoExpired(expirationTarget)
                ? 'Réactiver le compte démo'
                : 'Expiration du compte démo'}
            </DialogTitle>
            <DialogDescription>
              {`Choisissez la nouvelle durée d’accès de ${expirationTarget?.email ?? ''}, ou levez l’expiration.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2" role="group" aria-label="Nouvelle durée du compte démo">
            {demoExpirationChoices.map((option) => (
              <button
                key={option.value}
                type="button"
                data-testid={`demo-expiration-choice-${option.value}`}
                disabled={isExpirationSaving}
                onClick={() => setExpirationChoice(option.value)}
                className={cn(
                  'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
                  expirationChoice === option.value
                    ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted/40',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isExpirationSaving}
              onClick={() => setExpirationTarget(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              data-testid="demo-expiration-confirm"
              disabled={isExpirationSaving}
              onClick={() => void handleSaveDemoExpiration()}
            >
              {isExpirationSaving ? <Loader2Icon className="animate-spin" /> : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="right" className="w-full border-border sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>
              Ajuste le rôle, le statut et la vérification sans quitter la table.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Rôle</h3>
              <div className="flex flex-wrap gap-2">
                {roleFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setRoleFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      roleFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Type de compte</h3>
              <div className="flex flex-wrap gap-2">
                {accountTypeFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setAccountTypeFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      accountTypeFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Statut</h3>
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      statusFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Vérification</h3>
              <div className="flex flex-wrap gap-2">
                {verificationFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setVerificationFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      verificationFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <SheetFooter className="border-t border-border sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={() => {
              clearRoleFilter();
              clearAccountTypeFilter();
              clearStatusFilter();
              clearVerificationFilter();
            }}
            >
              Réinitialiser
            </Button>
            <Button onClick={() => setIsFilterSheetOpen(false)}>
              Appliquer
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
